import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { RootState } from '../../../store';
import { alertService } from '../../../shared/services/alertService';
import { useBreadcrumb } from '../../../layouts/BreadcrumbContext';
import {
  useGetSubjectQuery,
  useLazyEditCounsellingQuery,
  useLazyGetRcQuery,
  usePostSingleRcMutation,
  useLazyGetSignedUrlQuery,
  useUploadFileMutation,
  useLazyGetUploadedFilesQuery,
} from '../counselling.services';
import {
  RcRoleContext,
  RcRow,
  computeRcFlag,
  buildRcRow,
  shouldAddRc,
} from './rcFilters';
import { SUBJECT_DESIGNATIONS } from 'shared/services/commonService';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `OrderCopyDetailsComponent`
 * (src/app/views/pages/counselling/order-copy-details).
 *
 * RC (order copy) number entry: for a counselling (IndexId passed via router
 * state), renders a per-district/state row list where officers enter RC Number,
 * Officer Name and upload a signature, then save each row. Reached from DEE's
 * clickStart on the 'order-copy' path.
 */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

interface Option {
  label: string;
  value: string;
}

const OrderCopyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSelector((state: RootState) => state.session);
  const { setSubItems, clearSubItems } = useBreadcrumb();

  // Hand-off from DEE.clickStart comes via router navigation state (clean URL).
  const navState = (location.state ?? {}) as {
    path?: unknown;
    subject?: unknown;
  };

  const counId = useMemo(() => String(navState.path ?? ''), [navState.path]);
  const startSubject = useMemo(
    () => String(navState.subject ?? ''),
    [navState.subject]
  );

  const role: RcRoleContext = useMemo(
    () => ({
      district: Number(session.userId ?? 0),
      userType: str(session.usertypeid),
      userType1: str(session.emisUsertype1),
    }),
    [session.userId, session.usertypeid, session.emisUsertype1]
  );

  const { data: subjectRaw } = useGetSubjectQuery();
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [triggerGetRc] = useLazyGetRcQuery();
  const [postSingleRc] = usePostSingleRcMutation();
  const [triggerSignedUrl] = useLazyGetSignedUrlQuery();
  const [uploadFile] = useUploadFileMutation();
  const [triggerUploadedFiles] = useLazyGetUploadedFilesQuery();

  const [counsellingDetails, setCounsellingDetails] = useState<any>(null);
  const [subject, setSubject] = useState<string>('');
  const [cnsnSubList, setCnsnSubList] = useState<Option[]>([]);
  const [rcRows, setRcRows] = useState<RcRow[]>([]);
  const [photoUrl, setPhotoUrl] = useState('');
  const [viewImageModal, setViewImageModal] = useState(false);

  const subjectList: Option[] = useMemo(
    () => subjectRaw?.AllSubject ?? [],
    [subjectRaw]
  );

  const isSubjectDesig = useMemo(() => {
    const d = str(counsellingDetails?.Designation);
    return SUBJECT_DESIGNATIONS.includes(d);
  }, [counsellingDetails]);

  // ── getRc + filter (Angular getRcVal) ──────────────────────────────────────
  const getRcVal = useCallback(
    async (details: any, subjectVal: string) => {
      if (!details) return;
      const loc = computeRcFlag(details, role);
      const sub = role.userType === '5' ? subjectVal : (startSubject ?? '');
      try {
        const data: any = await triggerGetRc({
          counsellingId: counId,
          rcFlag: loc,
          subject: sub,
        }).unwrap();
        if (data?.dataStatus) {
          const rows = (data.result ?? [])
            .filter((val: any) => shouldAddRc(val, details, role))
            .map((val: any) => buildRcRow(val, details, role));
          setRcRows(rows);
        } else {
          setRcRows([]);
        }
      } catch {
        setRcRows([]);
      }
    },
    [counId, role, startSubject, triggerGetRc]
  );

  // ── GetOneCounselling (Angular) ─────────────────────────────────────────────
  const getOneCounselling = useCallback(async () => {
    try {
      const res: any = await triggerEditCounselling({
        counsellingId: counId,
      }).unwrap();
      if (res?.dataStatus && (res.result ?? []).length > 0) {
        const details = res.result[0];
        setCounsellingDetails(details);

        // Load RC rows unless type-5 on a subject designation (needs a subject
        // selection first).
        const d = str(details.Designation);
        const isType5SubjectDesig =
          role.userType === '5' &&
          ['11', '103', '36', '7', '138', '139'].includes(d);
        if (!isType5SubjectDesig) {
          getRcVal(details, '');
        }

        // Build the subject dropdown list from the counselling's Subject string.
        if (typeof details.Subject === 'string') {
          const list = details.Subject.split(',')
            .map((sid: string) => {
              const found = subjectList.filter(
                (pt) => str(pt.value) === str(sid)
              );
              return found.length > 0 ? found[0] : null;
            })
            .filter(Boolean) as Option[];
          setCnsnSubList(list);
        }
      } else {
        alertService.error('Something went wrong');
      }
    } catch {
      alertService.error('Something went wrong');
    }
  }, [counId, role.userType, subjectList, triggerEditCounselling, getRcVal]);

  useEffect(() => {
    if (!subjectRaw) return;
    getOneCounselling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectRaw, counId]);

  // Subject change → reload RC rows (Angular handleRcStaff).
  const handleRcStaff = useCallback(
    (value: string) => {
      setSubject(value);
      getRcVal(counsellingDetails, value);
    },
    [counsellingDetails, getRcVal]
  );

  // ── Row field updates ───────────────────────────────────────────────────────
  const updateRow = useCallback(
    (index: number, field: keyof RcRow, value: any) => {
      setRcRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
      );
    },
    []
  );

  // ── File upload (Angular onSelectFile) ──────────────────────────────────────
  const onSelectFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const files = e.target.files;
      if (!files || !files[0]) return;
      const file = files[0];
      if (file.size > 10000000) {
        alertService.warning(
          'Image Can`t uploaded because Image size should have less than or equal to 10mb'
        );
        return;
      }
      const splitName = file.name.split('.');
      const ext = splitName[1];
      if (ext !== 'png' && ext !== 'jpg' && ext !== 'jpeg') {
        alertService.error('File must be a valid format');
        return;
      }
      try {
        const result: any = await triggerSignedUrl({
          bucketName: 'renewalapplicationemis',
          ext,
          fileName: splitName[0],
          expiry: 300,
        }).unwrap();
        if (result?.url) {
          await uploadFile({ signedUrl: result.url, file }).unwrap();
          updateRow(index, 'SignatureKey', result.key);
          alertService.success('File Uploaded Successfully');
        } else {
          alertService.error('Error in Uploading File please try again');
        }
      } catch {
        alertService.error('Error in Uploading File please try again');
      }
    },
    [triggerSignedUrl, uploadFile, updateRow]
  );

  // ── View uploaded signature (Angular getUploadedFiles) ──────────────────────
  const getUploadedFiles = useCallback(
    async (filename: string) => {
      try {
        const result: any = await triggerUploadedFiles({
          bucketName: 'renewalapplicationemis',
          fileName: filename,
          expiry: 1800,
        }).unwrap();
        if (result?.url) {
          setPhotoUrl(result.url);
          setViewImageModal(true);
        } else {
          alertService.error('Error in Uploading File please try again');
        }
      } catch {
        alertService.error('Error in Uploading File please try again');
      }
    },
    [triggerUploadedFiles]
  );

  // ── Per-row save (Angular postRC) ───────────────────────────────────────────
  const postRC = useCallback(
    async (row: RcRow, index: number) => {
      if (!row.RcNo) {
        alertService.error('Please fill RC Number');
        return;
      }
      const loc = str(counsellingDetails?.Location);
      const rcFlag =
        role.district === 16 &&
        role.userType === '10' &&
        role.userType1 === '2' &&
        loc === '1'
          ? '2'
          : str(counsellingDetails?.schType) === '2' &&
              loc === '4' &&
              role.userType === '10' &&
              role.userType1 === '3'
            ? '2'
            : counsellingDetails?.Location;

      const req = {
        records: {
          CnslId: counId,
          Subject: role.userType === '5' ? subject : (startSubject ?? ''),
          RcFlag: rcFlag,
          UserId: loc === '3' || loc === '5' ? '24' : row.UserId,
          RcNo: row.RcNo,
          OtherNme: row.OtherNme,
          Sign: row.SignatureKey,
          IndexId: row.IndexId,
        },
      };
      try {
        const res: any = await postSingleRc(req).unwrap();
        if (res?.dataStatus) {
          alertService.success(res.message);
          if (res.IndxID != null) {
            updateRow(index, 'IndexId', String(res.IndxID));
          }
        } else {
          alertService.error('Something went wrong. Please try again');
        }
      } catch {
        alertService.error('Something went wrong. Please try again');
      }
    },
    [
      counId,
      counsellingDetails,
      role,
      subject,
      startSubject,
      postSingleRc,
      updateRow,
    ]
  );

  // ── Navigation ──────────────────────────────────────────────────────────────
  const handleCandidateURL = useCallback(
    () => navigate('/counselling/front-page'),
    [navigate]
  );
  const handleCounsellingURL = useCallback(() => navigate(-1), [navigate]);
  // Back to the DEE order-copy list this page was opened from.
  const handleDeeURL = useCallback(
    () => navigate('/counselling/dee?path=order-copy'),
    [navigate]
  );

  // Feed the app-shell breadcrumb (like the DEE page). Two role variants:
  //   userType 9 → Update Order Copy Details / <Title>
  //   others     → Plan New Counselling / Order Copy Details
  useEffect(() => {
    if (role.userType === '9') {
      setSubItems([
        { label: 'Counselling', onClick: handleCandidateURL },
        // Back to the DEE order-copy list (the page we navigated from).
        { label: 'Update Order Copy Details', onClick: handleDeeURL },
        { label: counsellingDetails?.Title ?? 'Order Copy Details' },
      ]);
    } else {
      setSubItems([
        { label: 'Plan New Counselling', onClick: handleCounsellingURL },
        { label: 'Order Copy Details' },
      ]);
    }
    return () => clearSubItems();
  }, [
    role.userType,
    counsellingDetails,
    handleCandidateURL,
    handleCounsellingURL,
    handleDeeURL,
    setSubItems,
    clearSubItems,
  ]);

  // Read-only rule (Angular template): editable when location 3/5 + user 9,
  // or when location is neither 3 nor 5.
  const loc = str(counsellingDetails?.Location);
  const isReadOnly = !(
    ((loc === '3' || loc === '5') && role.userType !== '9') ||
    (loc !== '3' && loc !== '5')
  );

  return (
    <div className="main-body-content">
      {/* Breadcrumb handled by the app shell (see the useBreadcrumb effect). */}
      <div className="stepsdemo-content">
        <div className="card p-4">
          {/* Subject dropdown — type-5 subject designations only */}
          {role.userType === '5' && isSubjectDesig && (
            <div className="row">
              <div className="col-4" />
              <div className="col-4">
                <div className="row">
                  <div className="col-2 mt-2 text-right text-end">
                    <label>Subject</label>
                  </div>
                  <div className="col">
                    <Dropdown
                      value={subject}
                      options={cnsnSubList}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select a subject"
                      showClear
                      onChange={(e) => handleRcStaff(e.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="col-4" />
            </div>
          )}

          <br />

          {/* RC table header */}
          <div className="row">
            <div className="col-1">
              <b>S.NO</b>
            </div>
            <div className="col-2">
              <b>{loc !== '3' ? 'District' : 'State'}</b>
            </div>
            <div className="col">
              <b>RC Number</b>
            </div>
            <div className="col">
              <b>Officer Name</b>
            </div>
            <div className="col">
              <b>Signature</b>
            </div>
            <div className="col-1">
              <b>Action</b>
            </div>
          </div>

          {/* RC rows */}
          <div className="form-group">
            {rcRows.map((item, i) => (
              <div className="row mt-4" key={`${item.IndexId ?? 'rc'}-${i}`}>
                <div className="col-1 mt-3">{i + 1}</div>
                <div className="col-2 mt-2">{item.DistrictName}</div>
                <div className="col">
                  <InputText
                    value={item.RcNo ?? ''}
                    placeholder="Enter RC Number"
                    className="form-control"
                    readOnly={isReadOnly}
                    onChange={(e) => updateRow(i, 'RcNo', e.target.value)}
                  />
                </div>
                <div className="col">
                  <InputText
                    value={item.OtherNme ?? ''}
                    placeholder="Enter officer name"
                    className="form-control"
                    readOnly={isReadOnly}
                    onChange={(e) => updateRow(i, 'OtherNme', e.target.value)}
                  />
                </div>
                <div className="col">
                  <input
                    type="file"
                    className="form-control"
                    disabled={isReadOnly}
                    onChange={(e) => onSelectFile(e, i)}
                  />
                  <small>
                    <b className="note-title">Note:</b>{' '}
                    <i className="note">
                      (Only PNG, JPEG, JPG files with a maximum size of 10MB)
                    </i>
                  </small>
                </div>
                <div
                  className="col-1 d-flex align-items-start"
                  style={{ flexWrap: 'nowrap', gap: '0.5rem' }}
                >
                  {item.SignatureKey && (
                    <Button
                      type="button"
                      icon="pi pi-eye"
                      className="p-button-primary"
                      onClick={() => getUploadedFiles(item.SignatureKey)}
                    />
                  )}
                  <Button
                    type="button"
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={() => postRC(item, i)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Signature view dialog */}
      <Dialog
        header="Signature"
        modal
        appendTo={document.body}
        visible={viewImageModal}
        onHide={() => setViewImageModal(false)}
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '50vw' }}
      >
        <div className="mt-4">
          <img src={photoUrl} width="100%" height="100%" alt="signature" />
        </div>
      </Dialog>
    </div>
  );
};

export default OrderCopyDetails;

/* eslint-enable @typescript-eslint/no-explicit-any */
